// =============================================================================
// ChatInput.tsx — Text input with attach and send buttons (web port)
// =============================================================================

import React, { useCallback } from 'react';
import { Box, TextField, IconButton, Badge } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ChatInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onSend: () => void;
  sendDisabled: boolean;
  onAttachPress: () => void;
  pendingPhotoCount: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChangeText,
  onSend,
  sendDisabled,
  onAttachPress,
  pendingPhotoCount,
}) => {
  const { theme } = useTheme();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !sendDisabled) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend, sendDisabled]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        px: 1.5,
        py: 1,
        backgroundColor: theme.background1,
        borderTop: `1px solid ${theme.background2}`,
      }}
    >
      {/* Attachment button */}
      <Badge
        badgeContent={pendingPhotoCount > 0 ? pendingPhotoCount : undefined}
        color="primary"
        sx={{ mr: 1, mb: 0.5 }}
      >
        <IconButton
          onClick={onAttachPress}
          aria-label="Add attachment"
          size="small"
          sx={{ color: theme.primary }}
        >
          <AttachFileIcon />
        </IconButton>
      </Badge>

      {/* Text input */}
      <TextField
        value={value}
        onChange={(e) => onChangeText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        multiline
        maxRows={4}
        inputProps={{ maxLength: 5000 }}
        aria-label="Message input"
        size="small"
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: '18px',
            backgroundColor: theme.background2,
            fontSize: 14,
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: theme.stroke,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.primary,
            },
          },
          '& .MuiInputBase-input': {
            color: theme.text,
            py: 1,
            px: 1.75,
          },
        }}
      />

      {/* Send button */}
      <IconButton
        onClick={onSend}
        disabled={sendDisabled}
        aria-label="Send message"
        size="small"
        sx={{
          ml: 1,
          mb: 0.5,
          color: sendDisabled ? theme.secondary : theme.primary,
          opacity: sendDisabled ? 0.4 : 1,
        }}
      >
        <SendIcon />
      </IconButton>
    </Box>
  );
};

export default ChatInput;
